// Package Manager for .sampkg files
// Handles loading, saving, and validation of survey packages

/**
 * Package file format (.sampkg):
 * {
 *   packageInfo: {
 *     id: string,
 *     title: string,
 *     version: string,
 *     author: string,
 *     description: string,
 *     variables: string[], // For conflict detection
 *     createdDate: string,
 *     questionCount: number
 *   },
 *   surveyData: {
 *     title: string,
 *     description: string,
 *     nodes: array,
 *     edges: array
 *   }
 * }
 */

export const createPackageFromSurvey = (surveyData, nodes, edges, packageInfo) => {
  // Extract variables from question nodes
  const variables = nodes
    .filter(node => node.type === 'question' && node.data.variable)
    .map(node => node.data.variable);

  // Count question nodes
  const questionCount = nodes.filter(node => node.type === 'question').length;

  const packageData = {
    packageInfo: {
      id: packageInfo.id || `package-${Date.now()}`,
      title: packageInfo.title || surveyData.title || 'Untitled Package',
      version: packageInfo.version || '1.0',
      author: packageInfo.author || 'Unknown',
      description: packageInfo.description || surveyData.description || '',
      variables: variables,
      createdDate: new Date().toISOString(),
      questionCount: questionCount
    },
    surveyData: {
      title: surveyData.title,
      description: surveyData.description,
      estimatedDuration: surveyData.estimatedDuration,
      author: surveyData.author,
      version: surveyData.version,
      nodes: nodes,
      edges: edges
    }
  };

  return packageData;
};

export const validatePackageStructure = (nodes, edges) => {
  const errors = [];
  const warnings = [];

  // Check for exactly one start node
  const startNodes = nodes.filter(node => node.type === 'start');
  if (startNodes.length === 0) {
    errors.push('Package must have exactly one Start node');
  } else if (startNodes.length > 1) {
    errors.push('Package cannot have multiple Start nodes');
  }

  // Check for exactly one end node
  const endNodes = nodes.filter(node => node.type === 'end');
  if (endNodes.length === 0) {
    errors.push('Package must have exactly one End node');
  } else if (endNodes.length > 1) {
    errors.push('Package cannot have multiple End nodes');
  }

  // Check for question nodes with variables
  const questionNodes = nodes.filter(node => node.type === 'question');
  const nodesWithoutVariables = questionNodes.filter(node => !node.data.variable || node.data.variable.trim() === '');
  
  if (nodesWithoutVariables.length > 0) {
    warnings.push(`${nodesWithoutVariables.length} question(s) don't have variable names defined`);
  }

  // Check for variable uniqueness
  const variables = questionNodes
    .filter(node => node.data.variable && node.data.variable.trim() !== '')
    .map(node => node.data.variable.trim());
  
  const duplicateVariables = variables.filter((variable, index) => variables.indexOf(variable) !== index);
  if (duplicateVariables.length > 0) {
    errors.push(`Duplicate variable names found: ${[...new Set(duplicateVariables)].join(', ')}`);
  }

  // Check connectivity - all nodes should be reachable from start
  if (startNodes.length === 1 && endNodes.length === 1) {
    const reachableNodes = getReachableNodes(startNodes[0].id, edges);
    const unreachableNodes = nodes.filter(node => 
      node.id !== startNodes[0].id && !reachableNodes.has(node.id)
    );
    
    if (unreachableNodes.length > 0) {
      warnings.push(`${unreachableNodes.length} node(s) are not reachable from Start node`);
    }

    // Check if end node is reachable
    if (!reachableNodes.has(endNodes[0].id)) {
      errors.push('End node is not reachable from Start node');
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

const getReachableNodes = (startNodeId, edges) => {
  const reachable = new Set();
  const queue = [startNodeId];

  while (queue.length > 0) {
    const currentId = queue.shift();
    if (reachable.has(currentId)) continue;
    
    reachable.add(currentId);
    
    // Find all outgoing edges from current node
    const outgoingEdges = edges.filter(edge => edge.source === currentId);
    outgoingEdges.forEach(edge => {
      if (!reachable.has(edge.target)) {
        queue.push(edge.target);
      }
    });
  }

  return reachable;
};

export const detectVariableConflicts = (parentVariables, packageVariables) => {
  const conflicts = packageVariables.filter(variable => 
    parentVariables.includes(variable)
  );
  
  return {
    hasConflicts: conflicts.length > 0,
    conflicts: conflicts
  };
};

export const loadPackageFromFile = async (fileHandle) => {
  try {
    const file = await fileHandle.getFile();
    const content = await file.text();
    const packageData = JSON.parse(content);
    
    // Validate package format
    if (!packageData.packageInfo || !packageData.surveyData) {
      throw new Error('Invalid package format: missing packageInfo or surveyData');
    }

    // Validate required packageInfo fields
    const requiredFields = ['id', 'title', 'version', 'variables'];
    for (const field of requiredFields) {
      if (!packageData.packageInfo[field]) {
        throw new Error(`Invalid package format: missing packageInfo.${field}`);
      }
    }

    return {
      success: true,
      data: packageData
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

export const savePackageToFile = async (packageData, fileName) => {
  try {
    const fileHandle = await window.showSaveFilePicker({
      suggestedName: fileName.endsWith('.sampkg') ? fileName : `${fileName}.sampkg`,
      types: [{
        description: 'Survey Assistant Module Package',
        accept: { 'application/json': ['.sampkg'] }
      }]
    });

    const writable = await fileHandle.createWritable();
    await writable.write(JSON.stringify(packageData, null, 2));
    await writable.close();

    return {
      success: true,
      fileHandle: fileHandle
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

export const extractVariablesFromSurvey = (nodes) => {
  return nodes
    .filter(node => node.type === 'question' && node.data.variable)
    .map(node => node.data.variable.trim())
    .filter(variable => variable !== '');
}; 